import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";

import { useEffect, useMemo, useState } from "react";
import { DialogBackdrop } from "@headlessui/react";
import { motion } from "motion/react";
import Button from "../Button";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-hot-toast";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import FormInput from "../FormInput";
import { useMutation, useLazyQuery } from "@apollo/client";
import useEventDetails from "../../pages/events/pages/event/hooks/useEventDetails";
import {
  CreateDocumentDocument,
  DocumentFragment,
  DocumentType,
  EventDetailsDocument,
  OrganizationDocumentsDocument,
  ProjectDetailsDocument,
  SharingMode,
  SignedUrlToUploadFileDocument,
} from "../../__generated__/graphql";
import useSignedUrlToUploadFile from "../../hooks/useSignedUrlToUploadFile";
import Checkbox from "../Checkbox";
import {
  communicationTypeToName,
  DocumentPropertiesType,
  documentTypeToName,
} from "../../lib/utils";
import DragDropFileUpload from "../DragDropFileUpload";
import {
  XMarkIcon,
  Bars3Icon,
  UserPlusIcon,
} from "@heroicons/react/24/outline";
import ParticipantsSelectionModal from "../ParticipantsSelectionModal";
import SelectedMembersDisplay from "../SelectedMembersDisplay";
import { PublicUserOrganization, UserGroupFragment } from "../../__generated__/graphql";
import { UserGroupIcon } from "@heroicons/react/24/solid";

const DocumentPropertiesSchema = z.object({
  type: z.enum(Object.values(DocumentPropertiesType) as [string, ...string[]], {
    required_error: "Le type de publication est requis",
  }),
  otherType: z.string().optional(),
  legend: z.string().optional(),
});

const createDocumentSchema = z
  .object({
    file: z.instanceof(File).optional(),
    title: z.string().min(1, "Un titre est requis"),
    description: z.string().optional(),
    isPublic: z.boolean(),
    type: z.nativeEnum(DocumentType),
    properties: DocumentPropertiesSchema.optional(),
  })
  .refine(
    (data) => {
      // If type is Communication, properties.type is required
      if (data.type === DocumentType.Communication) {
        return data.properties?.type !== undefined;
      }
      return true;
    },
    {
      message:
        "Le type de publication est requis pour les documents de type Communication",
      path: ["properties", "type"],
    }
  )
  .refine(
    (data) => {
      // If type is Communication and properties.type is "other", otherType is required
      if (
        data.type === DocumentType.Communication &&
        data.properties?.type === DocumentPropertiesType.OTHER
      ) {
        return (
          data.properties?.otherType !== undefined &&
          data.properties.otherType.trim().length > 0
        );
      }
      return true;
    },
    {
      message: "Veuillez préciser le type de communication",
      path: ["properties", "otherType"],
    }
  );

type CreateDocumentFormData = z.infer<typeof createDocumentSchema>;

interface CreateDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId?: string;
  projectId?: string;
  onSubmit: (document: DocumentFragment) => void;
  canBeSharedWithMultiplePerson?: boolean;
  preSelectedDocumentType?: DocumentType;
}

export default function CreateDocumentModal({
  isOpen,
  onClose,
  eventId,
  projectId,
  onSubmit,
  canBeSharedWithMultiplePerson = false,
  preSelectedDocumentType,
}: CreateDocumentModalProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<CreateDocumentFormData>({
    resolver: zodResolver(createDocumentSchema),
    defaultValues: {
      isPublic: canBeSharedWithMultiplePerson,
      type: preSelectedDocumentType ?? DocumentType.Other,
    },
  });

  const [createDocument, { loading: isCreatingDocument }] = useMutation(
    CreateDocumentDocument
  );

  // Fetch event details to get projectId if document is under an event
  const { data: eventDetailsData } = useEventDetails(eventId);

  // Derive effective projectId: direct projectId OR from event's project
  const effectiveProjectId = useMemo(() => {
    if (projectId) return projectId;
    if (eventDetailsData?.event?.projectId) return eventDetailsData.event.projectId;
    return undefined;
  }, [projectId, eventDetailsData?.event?.projectId]);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  const [selectedParticipants, setSelectedParticipants] = useState<
    PublicUserOrganization[]
  >([]);
  const [selectedGroups, setSelectedGroups] = useState<UserGroupFragment[]>([]);
  const [isSelectingParticipants, setIsSelectingParticipants] = useState(false);
  const [isCarousel, setIsCarousel] = useState(false);
  const [carouselFiles, setCarouselFiles] = useState<
    Array<{
      id: string;
      file: File;
      bucketKey: string | null;
      mimeType: string;
      order: number;
      isUploading: boolean;
      isUploaded: boolean;
    }>
  >([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const [getSignedUrl] = useLazyQuery(SignedUrlToUploadFileDocument);

  useEffect(() => {
    reset({
      title: "",
      description: "",
      isPublic: canBeSharedWithMultiplePerson,
      type: preSelectedDocumentType ?? DocumentType.Other,
      properties: undefined,
    });
    setIsCarousel(false);
    setCarouselFiles([]);
    setSelectedFile(null);
    setSelectedParticipants([]);
    setSelectedGroups([]);
  }, [canBeSharedWithMultiplePerson, isOpen, preSelectedDocumentType, reset]);

  const documentType = watch("type");
  const isCommunicationType = documentType === DocumentType.Communication;
  const communicationType = watch("properties.type");
  const isOtherType = communicationType === DocumentPropertiesType.OTHER;

  // Clear properties when type changes away from Communication
  useEffect(() => {
    if (!isCommunicationType) {
      setValue("properties", undefined);
    }
  }, [isCommunicationType, setValue]);

  // Clear otherType when communication type changes away from "other"
  useEffect(() => {
    if (!isOtherType) {
      setValue("properties.otherType", undefined);
    }
  }, [isOtherType, setValue]);

  const generatedBucketKey = useMemo(() => {
    return uuidv4();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const {
    data: signedUrl,
    loading: signedUrlLoading,
    error: signedUrlError,
  } = useSignedUrlToUploadFile(generatedBucketKey);

  const handleFilesSelect = (files: File[]) => {
    if (isCarousel) {
      setCarouselFiles((prev) => {
        const newFiles = files.map((file, index) => ({
          id: uuidv4(),
          file,
          bucketKey: null,
          mimeType: file.type,
          order: prev.length + index,
          isUploading: false,
          isUploaded: false,
        }));
        return [...prev, ...newFiles];
      });
    } else {
      const file = files[0];
      setSelectedFile(file);
      // Set file in form
      setValue("file", file);
    }
  };

  const handleRemoveCarouselFile = (id: string) => {
    const updatedFiles = carouselFiles
      .filter((f) => f.id !== id)
      .map((f, index) => ({ ...f, order: index }));
    setCarouselFiles(updatedFiles);
  };

  const handleDragStart = (id: string) => {
    const index = carouselFiles.findIndex((f) => f.id === id);
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, id: string) => {
    e.preventDefault();
    if (draggedIndex === null) return;

    const targetIndex = carouselFiles.findIndex((f) => f.id === id);
    if (draggedIndex === targetIndex) return;

    const newFiles = [...carouselFiles];
    const draggedItem = newFiles[draggedIndex];
    newFiles.splice(draggedIndex, 1);
    newFiles.splice(targetIndex, 0, draggedItem);

    const reorderedFiles = newFiles.map((f, idx) => ({ ...f, order: idx }));
    setCarouselFiles(reorderedFiles);
    setDraggedIndex(targetIndex);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  if (signedUrlLoading || signedUrlError) {
    return null;
  }

  const onSubmitDocument = async (data: CreateDocumentFormData) => {
    if (isUploadingDocument) {
      return null;
    }

    // Validate carousel files
    if (isCarousel && carouselFiles.length === 0) {
      toast.error("Veuillez ajouter au moins un fichier au carrousel");
      return;
    }

    // Validate single file when not carousel
    if (!isCarousel && !data.file) {
      toast.error("Un fichier est requis");
      return;
    }

    try {
      setIsUploadingDocument(true);

      if (isCarousel && carouselFiles.length > 0) {
        // Sort files by order before uploading
        const sortedCarouselFiles = [...carouselFiles].sort(
          (a, b) => a.order - b.order
        );

        // Upload all carousel files first
        const uploadedCarouselFiles = await Promise.all(
          sortedCarouselFiles.map(async (carouselFile) => {
            const bucketKey = uuidv4();
            const { data: signedUrlData } = await getSignedUrl({
              variables: { bucketKey },
            });

            if (!signedUrlData?.signedUrlToUploadFile) {
              throw new Error("Failed to get signed URL for carousel file");
            }

            const response = await fetch(signedUrlData.signedUrlToUploadFile, {
              method: "PUT",
              body: carouselFile.file,
            });

            if (!response.ok) {
              throw new Error("Failed to upload carousel file");
            }

            return {
              bucketKey,
              mimeType: carouselFile.mimeType,
              order: carouselFile.order,
            };
          })
        );

        // Use the first carousel file's bucketKey as the main document bucketKey
        const mainBucketKey = uploadedCarouselFiles[0].bucketKey;
        const mainMimeType = uploadedCarouselFiles[0].mimeType;

        const createdDocument = await createDocument({
          variables: {
            createDocumentInput: {
              title: data.title,
              description: data.description,
              bucketKey: mainBucketKey,
              eventId: eventId,
              projectId: projectId,
              isPublic: data.isPublic,
              type: data.type,
              mimeType: mainMimeType,
              documentCarousel: uploadedCarouselFiles,
              properties: data.properties
                ? JSON.stringify(data.properties)
                : undefined,
              documentSharings: data.isPublic
                ? undefined
                : selectedParticipants.map((participant) => ({
                    recipientUserOrganizationId: participant.id,
                    recipientOrganizationId: participant.organizationId,
                    sharingMode: SharingMode.Shared,
                  })),
              documentGroupSharings: data.isPublic
                ? undefined
                : selectedGroups.map((group) => ({
                    userGroupId: group.id,
                  })),
            },
          },
          refetchQueries: [
            OrganizationDocumentsDocument,
            ...(projectId
              ? [
                  {
                    query: ProjectDetailsDocument,
                    variables: {
                      projectId: projectId,
                    },
                  },
                ]
              : []),
            ...(eventId
              ? [
                  {
                    query: EventDetailsDocument,
                    variables: {
                      eventId: eventId,
                    },
                  },
                ]
              : []),
          ],
        });

        if (!createdDocument.data?.createDocument) {
          throw new Error();
        }

        toast.success("Document créé avec succès");
        onSubmit(createdDocument.data.createDocument);
      } else {
        // Regular single file upload
        if (!data.file) {
          toast.error("Un fichier est requis");
          return;
        }

        if (!signedUrl || !signedUrl.signedUrlToUploadFile) {
          return null;
        }

        const response = await fetch(signedUrl.signedUrlToUploadFile, {
          method: "PUT",
          body: data.file,
        });

        if (!response.ok) {
          throw new Error();
        }

        const createdDocument = await createDocument({
          variables: {
            createDocumentInput: {
              title: data.title,
              description: data.description,
              bucketKey: generatedBucketKey,
              eventId: eventId,
              projectId: projectId,
              isPublic: data.isPublic,
              type: data.type,
              mimeType: data.file.type, // data.file is checked above
              properties: data.properties
                ? JSON.stringify(data.properties)
                : undefined,
              documentSharings: data.isPublic
                ? undefined
                : selectedParticipants.map((participant) => ({
                    recipientUserOrganizationId: participant.id,
                    recipientOrganizationId: participant.organizationId,
                    sharingMode: SharingMode.Shared,
                  })),
              documentGroupSharings: data.isPublic
                ? undefined
                : selectedGroups.map((group) => ({
                    userGroupId: group.id,
                  })),
            },
          },
          refetchQueries: [
            OrganizationDocumentsDocument,
            ...(projectId
              ? [
                  {
                    query: ProjectDetailsDocument,
                    variables: {
                      projectId: projectId,
                    },
                  },
                ]
              : []),
            ...(eventId
              ? [
                  {
                    query: EventDetailsDocument,
                    variables: {
                      eventId: eventId,
                    },
                  },
                ]
              : []),
          ],
        });

        if (!createdDocument.data?.createDocument) {
          throw new Error();
        }

        toast.success("Document créé avec succès");
        onSubmit(createdDocument.data.createDocument);
      }

      reset();
      setSelectedFile(null);
      setCarouselFiles([]);
      setIsCarousel(false);
      setIsUploadingDocument(false);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Une erreur est survenue lors de la création du document"
      );
      setIsUploadingDocument(false);
    }
  };

  const shareOptions = () => {
    return (
      <div className="flex flex-col gap-2">
        <div
          className="flex items-center gap-2 my-4 cursor-pointer text-secondary hover:text-primary transition-all duration-300"
          onClick={() => {
            setValue("isPublic", !watch("isPublic"));
          }}
        >
          <Checkbox checked={watch("isPublic")} onChange={() => {}} />
          <div className="text-sm ">Partager avec tous les participants</div>
        </div>

        {!watch("isPublic") ? (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="flex flex-col gap-2"
          >
            <div className="flex items-center justify-between">
              <span className="text-sm text-secondary">
                {selectedParticipants.length + selectedGroups.length > 0
                  ? `${selectedParticipants.length + selectedGroups.length} sélection${selectedParticipants.length + selectedGroups.length > 1 ? "s" : ""}`
                  : "Aucune sélection"}
              </span>
              <Button
                type="button"
                variant="secondary"
                className="gap-2"
                onClick={() => setIsSelectingParticipants(true)}
              >
                <UserPlusIcon className="w-4 h-4" />
                Sélectionner
              </Button>
            </div>

            {(selectedParticipants.length > 0 || selectedGroups.length > 0) && (
              <div className="flex flex-col gap-2">
                {/* Selected Groups */}
                {selectedGroups.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {selectedGroups.map((group) => (
                      <div
                        key={group.id}
                        className="flex items-center gap-1 px-2 py-1 bg-accent/20 text-accent rounded-full text-xs"
                      >
                        <UserGroupIcon className="w-3 h-3" />
                        {group.name}
                        <button
                          type="button"
                          className="ml-1 hover:text-accent/70"
                          onClick={() => {
                            setSelectedGroups(selectedGroups.filter((g) => g.id !== group.id));
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Selected Participants */}
                {selectedParticipants.length > 0 && (
                  <SelectedMembersDisplay
                    members={selectedParticipants}
                    onRemove={(memberId) => {
                      setSelectedParticipants(
                        selectedParticipants.filter((p) => p.id !== memberId)
                      );
                    }}
                  />
                )}
              </div>
            )}
          </motion.div>
        ) : null}
      </div>
    );
  };

  const documentCreateContent = () => {
    return (
      <div className="flex flex-col gap-4 min-w-[400px]">
        <form
          onSubmit={handleSubmit(onSubmitDocument)}
          className="flex flex-col gap-4"
        >
          <FormInput
            id="title"
            label="Titre"
            register={register}
            error={errors.title}
            dataTestId="document-title-input"
          />

          <div>
            <label className="block text-sm font-medium mb-1">
              Description (optionnel)
            </label>
            <textarea
              {...register("description")}
              rows={2}
              className="w-full p-2 rounded-md bg-input focus:outline-none focus:ring-2 focus:ring-card border border-border"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Type de document
            </label>
            <select
              {...register("type")}
              id="projectType"
              className="w-full p-2 rounded-md bg-input focus:outline-none focus:ring-2 focus:ring-card border border-border placeholder:text-tertiary"
            >
              {Object.values(DocumentType).map((type) => (
                <option key={type} value={type}>
                  {documentTypeToName[type]}
                </option>
              ))}
            </select>
          </div>

          {isCommunicationType && (
            <>
              <div
                className="flex items-center gap-2 my-2 cursor-pointer text-secondary hover:text-primary transition-all duration-300"
                onClick={() => {
                  setIsCarousel(!isCarousel);
                  if (isCarousel) {
                    setCarouselFiles([]);
                  } else {
                    setSelectedFile(null);
                  }
                }}
              >
                <Checkbox checked={isCarousel} onChange={() => {}} />
                <div className="text-sm">Ce document est un carrousel</div>
              </div>

              <div className="flex flex-col gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Type de communication
                  </label>
                  <select
                    {...register("properties.type")}
                    className="w-full p-2 rounded-md bg-input focus:outline-none focus:ring-2 focus:ring-card border border-border placeholder:text-tertiary"
                  >
                    {Object.values(DocumentPropertiesType).map((type) => (
                      <option key={type} value={type}>
                        {communicationTypeToName(type)}
                      </option>
                    ))}
                  </select>
                  {errors.properties?.type && (
                    <p className="text-red-500 text-sm mt-1">
                      Le type de communication est requis
                    </p>
                  )}
                </div>

                {isOtherType && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <FormInput
                      id="properties.otherType"
                      label="Préciser le type de communication"
                      register={register}
                      error={errors.properties?.otherType}
                    />
                  </motion.div>
                )}

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Légende (optionnel)
                  </label>
                  <textarea
                    {...register("properties.legend")}
                    rows={3}
                    className="w-full p-2 rounded-md bg-input focus:outline-none focus:ring-2 focus:ring-card border border-border"
                    placeholder="Ce qui devrait être affiché sous la publication"
                  />
                </div>
              </div>
            </>
          )}

          {isCarousel ? (
            <div className="flex flex-col gap-4">
              <DragDropFileUpload
                onFilesSelect={handleFilesSelect}
                selectedFile={null}
                disabled={isUploadingDocument}
                placeholder="Glissez-déposez un ou plusieurs documents ici ou cliquez pour sélectionner"
                maxFileSize={50 * 1024 * 1024} // 50MB
                multiple={true}
              />
              {carouselFiles.length === 0 && (
                <p className="text-red-500 text-sm">
                  Au moins un fichier est requis
                </p>
              )}
              {carouselFiles.length > 0 && (
                <div className="flex flex-col gap-2 mt-2">
                  <div className="text-sm font-medium mb-2">
                    Fichiers du carrousel (glissez pour réorganiser) :
                  </div>
                  {carouselFiles
                    .sort((a, b) => a.order - b.order)
                    .map((carouselFile) => (
                      <div
                        key={carouselFile.id}
                        draggable
                        onDragStart={() => handleDragStart(carouselFile.id)}
                        onDragOver={(e) => handleDragOver(e, carouselFile.id)}
                        onDragEnd={handleDragEnd}
                        className="flex items-center gap-2 p-2 bg-input rounded-md border border-border cursor-move hover:border-primary transition-colors"
                      >
                        <Bars3Icon className="w-5 h-5 text-tertiary" />
                        <span className="flex-1 text-sm">
                          {carouselFile.file.name} (Ordre:{" "}
                          {carouselFile.order + 1})
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            handleRemoveCarouselFile(carouselFile.id)
                          }
                          className="text-red-500 hover:text-red-700 transition-colors"
                        >
                          <XMarkIcon className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                </div>
              )}
            </div>
          ) : (
            <>
              <DragDropFileUpload
                onFilesSelect={handleFilesSelect}
                selectedFile={selectedFile}
                disabled={isUploadingDocument}
                placeholder="Glissez-déposez un document ici ou cliquez pour sélectionner"
                maxFileSize={10000 * 1024 * 1024} // 10GB
              />
              {errors.file && (
                <p className="text-red-500 text-sm">Un fichier est requis</p>
              )}
            </>
          )}

          {canBeSharedWithMultiplePerson ? shareOptions() : null}

          <Button
            className="mt-4"
            type="submit"
            isLoading={isUploadingDocument || isCreatingDocument}
            dataTestId="document-submit-button"
          >
            Créer
          </Button>
        </form>
      </div>
    );
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={() => {
          onClose();
          setSelectedFile(null);
        }}
      >
        <DialogBackdrop className="fixed inset-0 bg-black/30" />
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="z-100 fixed inset-0 flex w-screen items-center justify-center p-4"
        >
          <DialogPanel data-testid="create-document-modal" className="max-w-lg space-y-4 bg-dark border border-input p-8 rounded-lg text-primary max-h-[100vh] overflow-y-auto custom-scrollbar">
            <DialogTitle className="font-bold text-xl">
              Nouveau document
            </DialogTitle>

            {documentCreateContent()}
          </DialogPanel>
        </motion.div>
      </Dialog>

      <ParticipantsSelectionModal
        isOpen={isSelectingParticipants}
        onClose={() => setIsSelectingParticipants(false)}
        onSubmit={(users, groups) => {
          setSelectedParticipants(users);
          if (groups) setSelectedGroups(groups);
        }}
        initialSelectedUsers={selectedParticipants}
        initialSelectedGroups={selectedGroups}
        title="Partager le document"
        description={effectiveProjectId ? "Sélectionnez les personnes ou groupes avec qui partager ce document." : "Sélectionnez les personnes avec qui partager ce document."}
        submitButtonText="Confirmer le partage"
        showGroups={!!effectiveProjectId}
        projectId={effectiveProjectId}
      />
    </>
  );
}
